/**
 * Premium gating primitives.
 *
 * Design intent (locked):
 *   - "hidden investigative archive" — never "cheap content blocker"
 *   - soft opacity reduction + cinematic vertical fade
 *   - NEVER heavy blur, NEVER mid-sentence cuts, NEVER popups
 *   - calm CTA: "Continue the investigation"
 *
 * Three primitives:
 *   <GhostCard>      — title-only premium echo (used for evidence/timeline/source rows)
 *   <PremiumFade>    — vertical gradient fade to #0B1220 + CTA card overlay
 *   <PremiumSection> — wraps an entire premium-locked block (real_world_examples,
 *                      unknowns, future_outlook, key_takeaways) with a calm CTA card
 */

import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { useMembership } from "../lib/membership";

// Final architecture (2026): all "Become a member" CTAs in the TCP archive
// route to the internal /membership interstitial. That page surfaces the
// 3 plan cards which deep-link directly into Shopify native subscription
// checkout via cart permalinks. After payment Shopify redirects the user
// to library.thecontextproject.com/activate-account?email={{customer.email}}.
const MEMBERSHIP_URL = process.env.REACT_APP_MEMBERSHIP_URL
    || "/membership";

export function ContinueInvestigationCTA({
    label = "You've reached the deeper layer of this investigation.",
    subline = "Members open the full pathway — sourced timelines, the source vault, ownership relationships, regional context, and the wider research archive.",
    href,
    compact = false,
    testId,
}) {
    // Resolve the membership URL at render-time, attaching a `next` param
    // so the user can be returned to where they were after checkout.
    // Shopify ignores arbitrary params, but they survive the round-trip
    // via the Customer Accounts referrer — the auth context picks them
    // Resolve the canonical TCP membership interstitial. Returning users land
    // on /membership → choose plan → deep-link into Shopify cart permalink.
    // Shopify's Order Status returns them to /activate-account after payment.
    const targetHref = href || MEMBERSHIP_URL;
    const isInternal = typeof targetHref === "string" && targetHref.startsWith("/");
    return (
        <div
            data-testid={testId || "premium-cta"}
            className={`relative overflow-hidden rounded-2xl border border-[#243044] bg-[#0F1828] ${compact ? "px-6 py-6" : "px-6 sm:px-8 py-8 sm:py-10"}`}
        >
            {/* Subtle accent rail */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#56CCF2]/50 to-transparent" />
            <div className="flex flex-col items-start gap-4 max-w-2xl">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#56CCF2]">
                    <Lock className="w-3 h-3" />
                    Research archive
                </div>
                <h3 className={`font-bold text-white tracking-tight ${compact ? "text-xl" : "text-2xl sm:text-3xl"}`}>
                    {label}
                </h3>
                <p className="text-sm sm:text-[15px] text-[#94A3B8] leading-relaxed">{subline}</p>
                <div className="flex flex-wrap items-center gap-4 pt-1">
                    {isInternal ? (
                        <Link
                            data-testid="premium-cta-join"
                            to={targetHref}
                            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#56CCF2] text-[#0B1220] text-sm font-semibold tracking-wide hover:bg-white transition-colors"
                        >
                            Open the deeper layer
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    ) : (
                        <a
                            data-testid="premium-cta-join"
                            href={targetHref}
                            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#56CCF2] text-[#0B1220] text-sm font-semibold tracking-wide hover:bg-white transition-colors"
                        >
                            Open the deeper layer
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    )}
                    <a
                        data-testid="premium-cta-signin"
                        href={typeof window !== "undefined" ? `/account/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}` : "/account/login"}
                        className="text-xs uppercase tracking-[0.2em] text-[#64748B] hover:text-[#56CCF2] transition-colors"
                    >
                        Member sign in
                    </a>
                </div>
            </div>
        </div>
    );
}

/**
 * Soft cinematic fade overlay rendered above ghost rows.
 * No blur — just opacity + gradient masking.
 */
export function PremiumFade({ children, height = 320, label, testId, ctaCompact = true }) {
    return (
        <div data-testid={testId || "premium-fade"} className="relative">
            <div
                className="relative"
                style={{
                    maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 55%, rgba(0,0,0,0.45) 80%, rgba(0,0,0,0) 100%)",
                    WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 55%, rgba(0,0,0,0.45) 80%, rgba(0,0,0,0) 100%)",
                    maxHeight: `${height}px`,
                    overflow: "hidden",
                }}
                aria-hidden="true"
            >
                {children}
            </div>
            <div className="mt-6">
                <ContinueInvestigationCTA compact={ctaCompact} label={label || "You've reached the deeper layer of this investigation."} />
            </div>
        </div>
    );
}

/**
 * Whole-section lock for premium-only blocks (Key takeaways, Real-world
 * examples, What's unclear, Where this could go).
 * Renders a calm placeholder card hinting at what lies beneath, plus the
 * documentary-style CTA.
 */
export function PremiumSection({ label, title, hint, count, testId }) {
    return (
        <section
            id={testId}
            data-testid={testId}
            className="py-10 sm:py-14 border-t border-[#243044]"
        >
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-3 inline-flex items-center gap-2">
                <Lock className="w-3 h-3" />
                {label}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3">
                {title}
            </h2>
            {hint && (
                <p className="text-sm text-[#64748B] mb-6 max-w-2xl">
                    {hint}
                    {typeof count === "number" && count > 0 ? ` · ${count} item${count === 1 ? "" : "s"} inside` : ""}
                </p>
            )}
            <ContinueInvestigationCTA compact testId={`${testId}-cta`} />
        </section>
    );
}

/**
 * Wraps any node and only renders it when the viewer is a member.
 * For non-members, returns a fallback (or null).
 */
export function MembersOnly({ children, fallback = null }) {
    const { isMember } = useMembership();
    if (!isMember) return fallback;
    return children;
}

/**
 * Renders `children` when the viewer is a NOT a member. Used for the lock
 * UI itself — so members never see the CTA noise.
 */
export function GuestsOnly({ children }) {
    const { isMember } = useMembership();
    if (isMember) return null;
    return children;
}
